
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EnhancedBookData } from './EnhancedBookSelectionCard';
import { BookFormat } from '@/types';

export const useBookFormLogic = (form: UseFormReturn<any>) => {
  const selectedBooks = form.watch('books') as EnhancedBookData[];
  
  // Group selected books by bookId
  const bookFormatGroups = React.useMemo(() => {
    const groups: Record<string, EnhancedBookData[]> = {};
    selectedBooks.forEach(book => {
      if (!groups[book.bookId]) {
        groups[book.bookId] = [];
      }
      groups[book.bookId].push(book);
    });
    return groups;
  }, [selectedBooks]);

  const toggleBook = (bookId: string, checked: boolean) => {
    const currentBooks = form.getValues('books') as EnhancedBookData[];
    if (checked) {
      // Add first format for the book
      const newFormat: EnhancedBookData = {
        bookId,
        unitsSold: 0,
        grossSales: 0,
        netSales: 0, // Previous Royalty Income - independent value
        royaltyAmount: 0, // Will be calculated as grossSales + netSales
        bookFormat: 'paperback',
        country: 'US',
        formatId: `${bookId}-${Date.now()}`
      };
      form.setValue('books', [...currentBooks, newFormat] as EnhancedBookData[]);
    } else {
      // Remove all formats for this book
      form.setValue('books', currentBooks.filter(book => book.bookId !== bookId) as EnhancedBookData[]);
    }
  };

  const addBookFormat = (bookId: string) => {
    const currentBooks = form.getValues('books') as EnhancedBookData[];
    const newFormat: EnhancedBookData = {
      bookId,
      unitsSold: 0,
      grossSales: 0,
      netSales: 0, // Previous Royalty Income - independent value
      royaltyAmount: 0, // Will be calculated as grossSales + netSales
      bookFormat: 'hardcover',
      country: 'US',
      formatId: `${bookId}-${Date.now()}`
    };
    form.setValue('books', [...currentBooks, newFormat] as EnhancedBookData[]);
  };

  const removeBookFormat = (formatId: string) => {
    const currentBooks = form.getValues('books') as EnhancedBookData[];
    form.setValue('books', currentBooks.filter(book => book.formatId !== formatId) as EnhancedBookData[]);
  };

  const updateBookData = (formatId: string, field: keyof Omit<EnhancedBookData, 'bookId' | 'formatId'>, value: number | BookFormat | string) => {
    const currentBooks = form.getValues('books') as EnhancedBookData[];
    const updatedBooks = currentBooks.map(book => {
      if (book.formatId === formatId) {
        const updatedBook = { ...book, [field]: value };
        
        // Auto-calculate royalty amount when gross sales or previous royalty income (netSales) changes
        if (field === 'grossSales' || field === 'netSales') {
          updatedBook.royaltyAmount = updatedBook.grossSales + updatedBook.netSales;
        }
        
        return updatedBook;
      }
      return book;
    });
    form.setValue('books', updatedBooks as EnhancedBookData[]);
  };

  const updateBookId = (formatId: string, newBookId: string) => {
    const currentBooks = form.getValues('books') as EnhancedBookData[];
    const updatedBooks = currentBooks.map(book => 
      book.formatId === formatId ? { ...book, bookId: newBookId } : book
    );
    form.setValue('books', updatedBooks as EnhancedBookData[]);
  };

  return {
    bookFormatGroups,
    toggleBook,
    addBookFormat,
    removeBookFormat,
    updateBookData,
    updateBookId
  };
};
